import React, { useCallback, useEffect, useState } from 'react';
import {
  getTeamLeaderAcademicCalendarSectionsPreview,
  type TeamLeaderAcademicCalendarImportPayload,
  type TeamLeaderAcademicCalendarSectionPreviewRow,
} from '../../services/academicCalendarTeamLeaderImportService';
import {
  addDays,
  countDays,
  countWeekdays,
  formatDateKor,
  isWeekday,
  isWeekend,
  parseISODate,
  toISODate,
} from '../../utils/date';

interface AcademicCalendarLeaderSectionImportModalProps {
  open: boolean;
  teamId: string;
  targetWorkspaceId: string;
  targetSectionId: string;
  onClose: () => void;
  onImported: (payload: TeamLeaderAcademicCalendarImportPayload) => void | Promise<void>;
}

function parseClassName(raw: string): { title: string; grades: number[] } {
  const [title, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const grades = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title, grades };
}

function formatShortDate(day: string | null | undefined): string {
  if (!day) return '-';
  const [, month, date] = day.split('-');
  if (!month || !date) return day;
  return `${Number(month)}.${Number(date)}`;
}

function formatFixedDate(day: string | null | undefined): string {
  if (!day) return '연도. 월. 일.';
  const [year, month, date] = day.split('-');
  if (!year || !month || !date) return day;
  return `${year}. ${month}. ${date}.`;
}

function computeSummary(section: TeamLeaderAcademicCalendarSectionPreviewRow) {
  const s = section.settings;
  if (!s?.year_starts_on || !s.year_ends_on || !s.summer_break_starts_on || !s.summer_break_ends_on) return null;
  const term1End = toISODate(addDays(parseISODate(s.summer_break_starts_on), -1));
  const term2Start = toISODate(addDays(parseISODate(s.summer_break_ends_on), 1));
  const inRange = (day: string, from: string, to: string) => day >= from && day <= to;
  const term1Holidays = section.days.filter((d) => inRange(d.day, s.year_starts_on, term1End) && isWeekday(d.day) && d.day_kind !== 'class').length;
  const term2Holidays = section.days.filter((d) => {
    if (!inRange(d.day, term2Start, s.year_ends_on)) return false;
    if (!isWeekday(d.day) || d.day_kind === 'class') return false;
    if (s.winter_break_starts_on && s.winter_break_ends_on && inRange(d.day, s.winter_break_starts_on, s.winter_break_ends_on)) return false;
    return true;
  }).length;
  const term1WeekendClass = section.days.filter((d) => d.day_kind === 'class' && isWeekend(d.day) && inRange(d.day, s.year_starts_on, term1End)).length;
  const term2WeekendClass = section.days.filter((d) => {
    if (d.day_kind !== 'class' || !isWeekend(d.day)) return false;
    if (!inRange(d.day, term2Start, s.year_ends_on)) return false;
    if (s.winter_break_starts_on && s.winter_break_ends_on && inRange(d.day, s.winter_break_starts_on, s.winter_break_ends_on)) return false;
    return true;
  }).length;
  let term1Days = Math.max(0, countWeekdays(s.year_starts_on, term1End) - term1Holidays + term1WeekendClass);
  let term2Days = Math.max(0, countWeekdays(term2Start, s.year_ends_on) - term2Holidays + term2WeekendClass);
  if (s.winter_break_starts_on && s.winter_break_ends_on) {
    term2Days = Math.max(0, term2Days - countWeekdays(s.winter_break_starts_on, s.winter_break_ends_on));
  }
  return {
    term1Start: s.year_starts_on,
    term1End,
    term2Start,
    term2End: s.year_ends_on,
    term1Days,
    term2Days,
    totalDays: term1Days + term2Days,
  };
}

function AcademicCalendarLeaderSectionCard({ section }: { section: TeamLeaderAcademicCalendarSectionPreviewRow }) {
  const settings = section.settings;
  const holidayDays = section.days
    .filter((d) => d.day_kind !== 'class')
    .sort((a, b) => a.day.localeCompare(b.day));
  const weekendClassDays = section.days
    .filter((d) => d.day_kind === 'class' && isWeekend(d.day))
    .sort((a, b) => a.day.localeCompare(b.day));
  const summary = computeSummary(section);

  return (
    <article className="cp-academic-leader-import-section-card is-selected">
      <div className="cp-academic-leader-import-section-head">
        <div className="card-summary pill cp-track-class-pill">클래스 공유</div>
        <h4>학사일정 수업일 설정</h4>
        <div className="cp-academic-leader-import-section-classes">
          {section.grade_labels.length === 0 ? (
            <span className="cp-academic-leader-import-empty-class">(연결 클래스 없음)</span>
          ) : (
            section.grade_labels.map((label, index) => {
              const parsed = parseClassName(label);
              return (
                <span key={`${label}-${index}`} className="cp-grade-track-class-block">
                  <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                  {parsed.grades.map((grade) => (
                    <span key={grade} className="class-grade-chip-display">
                      {grade}
                    </span>
                  ))}
                </span>
              );
            })
          )}
        </div>
      </div>

      <div className="cp-academic-leader-import-full-preview">
        <div className="academic-calendar-two-col">
          <article className="cp-card academic-calendar-settings-card cp-academic-leader-import-settings-card">
            <h3>학사일정 등록</h3>
            <div className="form-row academic-year-form">
              <label className="academic-year-label">
                <span className="cp-academic-leader-import-fixed-year">{settings ? settings.academic_year : '-'}</span>
                <span>학년도</span>
              </label>
            </div>
            <div className="form-row two-col">
              <label>
                학년도 시작일(시업식) <span className="required">*필수</span>
                <span className="cp-academic-leader-import-date-value">{formatFixedDate(settings?.year_starts_on)}</span>
              </label>
              <label>
                학년도 종료일(종업식/졸업식) <span className="required">*필수</span>
                <span className="cp-academic-leader-import-date-value">{formatFixedDate(settings?.year_ends_on)}</span>
              </label>
            </div>
            <div className="form-row two-col">
              <label>
                여름방학 시작일 <span className="required">*필수</span>
                <span className="cp-academic-leader-import-date-value">{formatFixedDate(settings?.summer_break_starts_on)}</span>
              </label>
              <label>
                여름방학 종료일 <span className="required">*필수</span>
                <span className="cp-academic-leader-import-date-value">{formatFixedDate(settings?.summer_break_ends_on)}</span>
              </label>
            </div>
            <div className="form-row two-col">
              <label>
                겨울방학 시작일 <span className="optional">*선택</span>
                <span className="cp-academic-leader-import-date-value">{formatFixedDate(settings?.winter_break_starts_on)}</span>
              </label>
              <label>
                겨울방학 종료일 <span className="optional">*선택</span>
                <span className="cp-academic-leader-import-date-value">{formatFixedDate(settings?.winter_break_ends_on)}</span>
              </label>
            </div>
          </article>

          <div className="academic-calendar-right-col">
            <article className="cp-card academic-calendar-holiday-card">
              <div className="holiday-card-title-row">
                <h3>공휴일 및 휴업일 등록</h3>
              </div>
              <div className="calendar-day-blocks">
                {holidayDays.length === 0 ? (
                  <p className="calendar-day-blocks-empty">등록된 공휴일 및 휴업일이 없습니다.</p>
                ) : (
                  holidayDays.map((day) => (
                    <div key={`${day.day}-${day.note ?? ''}`} className="calendar-day-block">
                      <span>
                        {formatShortDate(day.day)}
                        {day.note ? ` ${day.note}` : ''}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </article>

            <article className="cp-card academic-calendar-weekend-class-card">
              <div className="card-title-row card-title-row-with-desc">
                <h3>토요일, 일요일 수업일 등록</h3>
                <span className="card-title-desc">해당 날짜를 수업일로 포함합니다.</span>
              </div>
              <div className="calendar-day-blocks calendar-day-blocks-weekend-class">
                {weekendClassDays.length === 0 ? (
                  <p className="calendar-day-blocks-empty">등록된 토·일 수업일이 없습니다.</p>
                ) : (
                  weekendClassDays.map((day) => (
                    <div key={`${day.day}-${day.note ?? ''}`} className="calendar-day-block calendar-day-block-class">
                      <span>
                        {formatShortDate(day.day)}
                        {day.note ? ` ${day.note}` : ''}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </article>
          </div>
        </div>

        {settings && summary ? (
          <div className="academic-summary academic-summary-bottom academic-summary-two-col cp-academic-leader-import-summary">
            <div className="academic-summary-row">
              <p className="summary-line">
                1학기 : {formatDateKor(summary.term1Start)} ~ {formatDateKor(summary.term1End)}{' '}
                (수업일 수 : {summary.term1Days}일)
              </p>
              <p className="summary-line summary-line-sub">
                여름방학 : {formatDateKor(settings.summer_break_starts_on)} ~ {formatDateKor(settings.summer_break_ends_on)}{' '}
                (여름방학일 수 : {countDays(settings.summer_break_starts_on, settings.summer_break_ends_on)}일)
              </p>
            </div>
            <div className="academic-summary-row">
              <p className="summary-line">
                2학기 : {formatDateKor(summary.term2Start)} ~ {formatDateKor(summary.term2End)}{' '}
                (수업일 수 : {summary.term2Days}일)
              </p>
              <p className="summary-line summary-line-sub">
                {settings.winter_break_starts_on && settings.winter_break_ends_on ? (
                  <>
                    겨울방학 : {formatDateKor(settings.winter_break_starts_on)} ~ {formatDateKor(settings.winter_break_ends_on)}{' '}
                    (겨울방학일 수 : {countDays(settings.winter_break_starts_on, settings.winter_break_ends_on)}일)
                  </>
                ) : (
                  '겨울방학 : 겨울방학이 없습니다.'
                )}
              </p>
            </div>
            <p className="summary-line total academic-summary-full">총 수업일 수 : {summary.totalDays}일</p>
          </div>
        ) : null}
      </div>
    </article>
  );
}

const AcademicCalendarLeaderSectionImportModal: React.FC<AcademicCalendarLeaderSectionImportModalProps> = ({
  open,
  teamId,
  onClose,
  onImported,
}) => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<TeamLeaderAcademicCalendarSectionPreviewRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) setActiveIndex(0);
  }, [open]);

  useEffect(() => {
    setActiveIndex((i) => {
      if (sections.length === 0) return 0;
      return Math.min(Math.max(0, i), sections.length - 1);
    });
  }, [sections]);

  const loadPreview = useCallback(async () => {
    if (!open || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await getTeamLeaderAcademicCalendarSectionsPreview(teamId);
      setSections(payload.sections);
      setActiveIndex(0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [open, teamId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const selectedId = sections[activeIndex]?.section_id ?? null;
  const sectionCount = sections.length;
  const navDisabled = sectionCount <= 1;

  const handleImport = async () => {
    const selected = sections[activeIndex];
    if (!selected?.settings) {
      setError('선택한 섹션에 학사일정 설정이 없습니다.');
      return;
    }
    setImporting(true);
    setError(null);
    try {
      /** DB 즉시 저장 RPC 대신 미리보기 페이로드만 넘김 — 페이지에서 저장하기로 반영 */
      await onImported({ settings: selected.settings, days: selected.days });
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="cp-modal-backdrop cp-academic-leader-import-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cp-modal cp-academic-leader-import-modal" role="dialog" aria-modal="true" aria-labelledby="cp-academic-leader-import-title" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cp-leader-import-modal-title-row">
          <button
            type="button"
            className="cp-leader-import-modal-nav-btn"
            aria-label="이전 섹션"
            disabled={navDisabled || activeIndex <= 0}
            onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
          >
            &lt;
          </button>
          <h3 id="cp-academic-leader-import-title" className="cp-leader-import-modal-title-text">
            팀장 학사일정 가져오기
          </h3>
          <button
            type="button"
            className="cp-leader-import-modal-nav-btn"
            aria-label="다음 섹션"
            disabled={navDisabled || activeIndex >= sectionCount - 1}
            onClick={() => setActiveIndex((i) => Math.min(sectionCount - 1, i + 1))}
          >
            &gt;
          </button>
        </div>
        <p className="page-desc cp-academic-leader-import-intro">
          타이틀 양쪽 화살표로 팀장 섹션을 넘긴 뒤 가져오기를 누르면, 현재 섹션에 팀장 학사일정 설정과 등록된 휴업일/토·일 수업일이 적용됩니다.
          화면에는 바로 반영되며, 오른쪽 위 &quot;저장하기&quot;를 눌러야 서버에 저장됩니다.
        </p>
        {error ? <p className="cp-grade-track-leader-import-error">{error}</p> : null}
        <div className="cp-academic-leader-import-body">
          {loading ? (
            <p className="page-desc">불러오는 중...</p>
          ) : sections.length === 0 ? (
            <p className="page-desc">팀장 쪽에 표시할 섹션이 없습니다.</p>
          ) : (
            <div className="cp-academic-leader-import-section-list cp-leader-import-section-single">
              {sections[activeIndex] ? (
                <AcademicCalendarLeaderSectionCard key={sections[activeIndex].section_id} section={sections[activeIndex]} />
              ) : null}
            </div>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={importing}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => void handleImport()}
            disabled={!selectedId || !sections[activeIndex]?.settings || importing || loading}
          >
            {importing ? '가져오는 중...' : '가져오기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcademicCalendarLeaderSectionImportModal;
